import { Injectable, signal, inject, PLATFORM_ID, ApplicationRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { filter, concatMap, first } from 'rxjs/operators';
import { interval } from 'rxjs';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

@Injectable({
  providedIn: 'root'
})
export class PwaService {
  private swUpdate = inject(SwUpdate);
  private translate = inject(TranslateService);
  private platformId = inject(PLATFORM_ID);
  private appRef = inject(ApplicationRef);

  // Signal to check if installation is possible
  showInstallButton = signal(false);
  // Signal to notify that an update is available
  updateAvailable = signal(false);

  // Storage for the native event
  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  constructor() {
    this.initPwaFeatures();
  }

  private initPwaFeatures() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // 1. Handle Install Button
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      this.deferredPrompt = e as BeforeInstallPromptEvent;
      // Update UI notify the user they can install the PWA
      this.showInstallButton.set(true);
    });

    window.addEventListener('appinstalled', () => {
      this.showInstallButton.set(false);
      this.deferredPrompt = null;
    });

    // 2. Handle App Updates
    if (this.swUpdate.isEnabled) {
      // Polling for updates every 6 hours
      const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
      const everySixHours$ = interval(6 * 60 * 60 * 1000);
      const everySixHoursOnceAppIsStable$ = appIsStable$.pipe(concatMap(() => everySixHours$));

      everySixHoursOnceAppIsStable$.subscribe(() => {
        this.swUpdate
          .checkForUpdate()
          .catch((err) => console.error('Error checking for update', err));
      });

      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe(() => {
          // Notify the UI that a new version is ready
          this.updateAvailable.set(true);
        });
    }
  }

  installApp() {
    if (!this.deferredPrompt) {
      return;
    }
    this.deferredPrompt.prompt();
    this.deferredPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('Installation accepted');
      }
      this.deferredPrompt = null;
      this.showInstallButton.set(false);
    });
  }

  reloadApplication() {
    this.swUpdate.activateUpdate().then(() => {
      document.location.reload();
    });
  }
}
